import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { startTour, clearTourFlag } from "@/lib/productTour";
import { useNavigate } from "react-router-dom";
import {
  Search, ChevronRight, Rocket, Users, ClipboardList, Upload,
  Kanban as KanbanIcon, CalendarClock, Bell, TrendingUp, Mail,
  MessageSquare, Shield, Phone, FileText, KeyRound, Sparkles, PlayCircle,
  Smartphone, Undo2, Palette, Video, Linkedin,
} from "lucide-react";

const SECTIONS = [
  {
    id: "getting-started",
    icon: Rocket,
    title: "Getting started",
    superadmin: false,
    steps: [
      { h: "Sign in with your token", p: "Every user gets a login token from the SuperAdmin. Enter your email + token on the login screen. The token acts as your password — keep it safe." },
      { h: "The top bar", p: "The blue top bar shows the CERF LeadFlow logo, a global search (desktop only), the notification bell, and your profile initials. Hover the initials to see your name and role." },
      { h: "The left sidebar", p: "Your main navigation: Overview, Leads, Pipeline, Follow-ups, Upload Leads, Analytics, Guide. SuperAdmin also sees Members." },
      { h: "First-time product tour", p: "The first time you log in, a 60-second guided tour highlights every core feature. You can skip it, and restart it any time from the top of this Guide page." },
      { h: "Mobile", p: "On mobile the sidebar collapses into a hamburger menu (top-left). Tables scroll horizontally, and the Kanban board swipes between stages. You can install the app to your home screen from the banner on Overview." },
    ],
  },
  {
    id: "install-app",
    icon: Smartphone,
    title: "Install as an app (PWA)",
    superadmin: false,
    steps: [
      { h: "Why install?", p: "Adding LeadFlow to your home screen makes it launch like a native app — full screen, no browser tabs, and one-tap access. Perfect for reps who live on their phones." },
      { h: "On Android / Chrome desktop", p: "You'll see an 'Add LeadFlow to your home screen' banner at the top of Overview. Tap 'Install app' and confirm the browser prompt. Or use the browser menu → Install app." },
      { h: "On iPhone / iPad Safari", p: "The banner shows a tap-Share hint: tap Safari's Share icon, choose 'Add to Home Screen', then Add. The branded LeadFlow tile appears on your home screen." },
      { h: "Offline-friendly", p: "Once installed, the app shell (layout, icons, navigation) is cached so it loads instantly and works even on a spotty connection — data still needs internet, of course." },
      { h: "Dismissing the banner", p: "Not ready? Tap the X on the banner — it snoozes for 7 days. Once installed, the banner disappears permanently." },
    ],
  },
  {
    id: "leads-basics",
    icon: ClipboardList,
    title: "Working with leads",
    superadmin: false,
    steps: [
      { h: "Create a lead", p: "Go to Leads → New Lead. Required: Name. All other fields (phone, email, company, designation, budget, notes, follow-up date, tags, status, priority, source) are optional." },
      { h: "Multiple phones & emails", p: "Every lead now supports multiple phone numbers and emails. The primary phone/email is where WhatsApp / call / mail buttons point. Click 'Add another phone/email' in the Lead form to store secondaries — they're all checked in duplicate detection too." },
      { h: "Product & pricing block", p: "The lead form has a dedicated 'Product & pricing' section: Product (free text — e.g. 'LeadFlow Pro'), Account type (Prepaid / Postpaid), One-time Setup cost, Monthly recurring cost. These flow into the filters and the Lead Detail panel." },
      { h: "Duplicate alerts", p: "While typing a phone number or email, we auto-check for duplicates using normalised phone (spaces/+91 removed) and case-insensitive email — including secondary phones and emails. You'll see a warning card if a match exists." },
      { h: "Coloured pills for scanning", p: "Status, Priority and Source now use colour-coded pills. Statuses match the Kanban board (New=grey, Contacted=sky, Qualified=violet, Proposal=amber, Won=emerald, Lost=rose). Priority uses blue → amber → rose from Medium up. Sources each get their own tint. Scan a screen in a second." },
      { h: "Search & filter", p: "The search box scans name, phone, email, company, notes, secondary phones/emails, and product. Filters: status, priority, product, account type, and (SuperAdmin only) assigned member." },
      { h: "Edit / delete", p: "Click the ⋯ menu on any row to Edit or Delete. Click the row itself to open the Lead Detail panel." },
      { h: "Bulk actions", p: "Select multiple rows with checkboxes → a bulk action bar appears at the top: Change status, Priority, Reassign (SuperAdmin), or Delete." },
    ],
  },
  {
    id: "lead-detail",
    icon: FileText,
    title: "Lead detail & lifecycle",
    superadmin: false,
    steps: [
      { h: "Lifecycle stepper", p: "Every lead has a 5-stage visual stepper: New → Contacted → Qualified → Proposal → Won. Reaching Won shows a trophy. Setting status to Lost is shown separately." },
      { h: "Click-to-call / WhatsApp / Email", p: "Phone number is clickable (opens tel:), plus a WhatsApp button that jumps to wa.me. Email is a mailto: link." },
      { h: "Activity timeline", p: "Log notes, calls, meetings, or emails against a lead. Every status change is auto-logged too. Adding an activity also resets the 'abandoned' timer." },
      { h: "Reassign (SuperAdmin)", p: "Only SuperAdmin can reassign leads to a different member — use the Assign dropdown in the right column of the Lead Detail modal." },
    ],
  },
  {
    id: "pipeline-kanban",
    icon: KanbanIcon,
    title: "Pipeline (Kanban)",
    superadmin: false,
    steps: [
      { h: "Drag & drop", p: "Six columns (New, Contacted, Qualified, Proposal, Won, Lost). Drag any card between columns to update its status instantly — the change is optimistic and syncs to the server." },
      { h: "Colour-coded cards", p: "Each card is painted in the light tint of its stage with a stronger left accent bar, so the whole board is scannable at a glance. Dropping a card into a new column instantly repaints it — visual confirmation that the change stuck." },
      { h: "Card details", p: "Each card shows name, company, priority pill, budget, and follow-up date. Click a card to open its Lead Detail." },
      { h: "On mobile", p: "Columns swipe horizontally — one column at a time on narrow screens, so you can focus on the current stage without visual noise." },
    ],
  },
  {
    id: "upload-mapping",
    icon: Upload,
    title: "Bulk import with field mapping",
    superadmin: false,
    steps: [
      { h: "Choose your file", p: "Upload a CSV or Excel (.xlsx/.xls). Max 5 MB, up to 10,000 rows. Download the template if you want the exact column names." },
      { h: "Automatic detection", p: "We auto-map your columns to the 7 lead fields — name, phone, email, company, designation, budget, notes — using synonyms. 'Full Name' → name, 'Mobile' → phone, 'Job Title' → designation, 'Amount' → budget, 'Remarks' → notes, and many more." },
      { h: "Manually adjust", p: "Every mapping is a dropdown — override any auto-guess or pick '— Skip —' for columns you don't want to import." },
      { h: "Assign (SuperAdmin)", p: "SuperAdmin can assign the whole batch to a specific member from the 'Assign all to' dropdown. Members always import to themselves." },
      { h: "Duplicates on import", p: "Rows whose phone/email already exists (scoped to you if you're a member) are skipped and listed on the result screen." },
      { h: "Colour-based classification", p: "If you upload an .xlsx where you've highlighted rows with fill colours (e.g. red = hot, green = converted, yellow = follow-up), LeadFlow detects every distinct colour and shows a 'Colours detected' panel. Give each colour a tag name and matching rows will be imported with that tag — your visual triage survives the import. CSV files can't carry cell colours, so this step is skipped for CSVs." },
    ],
  },
  {
    id: "import-history-undo",
    icon: Undo2,
    title: "Import history & Undo",
    superadmin: false,
    steps: [
      { h: "Every import is a batch", p: "Every CSV/Excel upload is saved as an 'import batch' with filename, timestamp, uploader, and the exact leads it created. You'll find the panel below the upload form on the Upload Leads page." },
      { h: "One-click Undo", p: "Uploaded the wrong file? Click 'Undo import' on the batch — it deletes every lead created by that upload (only the ones still tagged with the same batch) plus their activity log. There's a confirmation prompt so you can't undo by accident." },
      { h: "Undone stays undone", p: "Once a batch is undone it's marked UNDONE and the Undo button disappears — you can't double-undo, and the batch stays in the history as an audit trail." },
      { h: "Scope", p: "Members see and can undo only their own imports. SuperAdmin sees and can undo every batch across the team." },
    ],
  },
  {
    id: "followups-alerts",
    icon: CalendarClock,
    title: "Follow-ups & alerts",
    superadmin: false,
    steps: [
      { h: "Follow-up date", p: "Set a follow-up date on any lead. The Follow-ups page groups leads into Overdue / Due Today / Upcoming." },
      { h: "Notification bell", p: "The bell in the top bar shows a live count of Overdue + Due Today + Abandoned leads. Click any item to jump to its Lead Detail." },
      { h: "Mark as read", p: "Hover any alert in the bell dropdown → click the X on the right to dismiss it. Or hit 'Mark all as read' at the top of the panel to clear the whole list. Dismissed alerts are hidden per-device (localStorage)." },
      { h: "Smart re-appear", p: "If a dismissed lead's state later changes — a new follow-up date is set, or activity is logged — the alert automatically reappears. You never permanently lose a reminder just because you marked it read." },
      { h: "Abandoned leads", p: "A lead with no activity for 14+ days (and not already overdue or due today) shows up as 'Abandoned' — a nudge to re-engage before the lead goes cold." },
    ],
  },
  {
    id: "email-reminders",
    icon: Mail,
    title: "Email reminders",
    superadmin: false,
    steps: [
      { h: "Auto-daily at 09:00 IST", p: "Every active user gets an email each morning listing their Overdue + Today + Abandoned leads, if there are any." },
      { h: "Manual triggers", p: "On the Follow-ups page: 'Email me my reminders' sends now to yourself; SuperAdmin can also 'Email whole team' (one email per member)." },
      { h: "Throttle", p: "To prevent spam, a member can only be emailed once every 6 hours by manual triggers. The daily 09:00 IST auto-run bypasses this. You'll see a toast if you're throttled." },
      { h: "Per-member trigger (SuperAdmin)", p: "On the Members page, each row has an 'Email' button that sends the reminder to just that member." },
    ],
  },
  {
    id: "analytics",
    icon: TrendingUp,
    title: "Analytics & insights",
    superadmin: false,
    steps: [
      { h: "Time-period filter", p: "Both Overview and Analytics have a Week / Month / Quarter / Year / All / Custom filter. New leads, Won, Conversion, and Revenue KPIs scope to the range; Pipeline Value + Follow-ups always reflect the current state." },
      { h: "Colour-coded charts", p: "The Overview pipeline chart uses one colour per stage (matching the Kanban board), and Source ROI bars cycle through a mixed palette so each source is instantly identifiable — no more solid blue everywhere." },
      { h: "Trend chart", p: "Weekly line chart of New vs Won for the selected period." },
      { h: "Cohort funnel", p: "For each week, see how many leads reached each stage — the true conversion signal (not just current snapshot)." },
      { h: "Source ROI", p: "Revenue by source, bar chart + detailed table (leads, won, conversion %, revenue, avg deal size)." },
      { h: "Member scorecards (SuperAdmin)", p: "Pick any member to see their handled/won/conversion/revenue/avg deal/pipeline/days-to-close/overdue/abandoned + last reminder timestamp. Team performance bars on Overview use varied colours so each rep is distinct." },
    ],
  },
  {
    id: "weekly-digest",
    icon: Sparkles,
    title: "Weekly digest",
    superadmin: true,
    steps: [
      { h: "Monday 09:00 IST", p: "Every Monday morning, every active SuperAdmin gets an HTML digest email: KPIs vs last week, top 5 performers, pipeline distribution, and Overdue/Abandoned counts." },
      { h: "Send on-demand", p: "Analytics page → 'Email weekly digest' fires it now, useful before a review meeting." },
    ],
  },
  {
    id: "members",
    icon: Users,
    title: "Managing members",
    superadmin: true,
    steps: [
      { h: "Create a member", p: "Members page → 'New Member'. Enter name, email, role (Member/SuperAdmin). Leave password blank to auto-generate a 12-char token — shown once, so copy it." },
      { h: "Regenerate token", p: "If a member forgets their token, click 'New token' on their row. The new token replaces the old one and is shown once." },
      { h: "Deactivate / reactivate", p: "Toggle the Active/Inactive pill on the row. Inactive users cannot log in." },
      { h: "Delete", p: "Removes the member and unassigns their leads (leads are not deleted)." },
    ],
  },
  {
    id: "teams-meeting",
    icon: Video,
    title: "Schedule Microsoft Teams meetings",
    superadmin: false,
    steps: [
      { h: "Open the lead", p: "On the Leads or Pipeline page, click any lead to open its Detail panel. In the right-hand sidebar you'll see an 'Actions' block with a purple 'Schedule Teams meeting' button." },
      { h: "Fill the invite", p: "Subject is pre-filled as 'Meeting with {lead name} — {company}'. Pick a date, start time, and duration (15/30/45/60/90/120 min). Attendees defaults to the lead's email; add teammates by comma-separating more emails. Add a short agenda." },
      { h: "Open in Teams", p: "Click 'Open in Teams' — a Teams window opens with your meeting pre-filled. Hit Send inside Teams to confirm; the meeting lands on your Teams / Outlook calendar and attendees get an invite email." },
      { h: "Auto-logged on the timeline", p: "The moment you open Teams, LeadFlow also logs a 'Meeting scheduled' activity on the lead's timeline — so the rest of the team sees the follow-up without you doing extra work." },
      { h: "No setup required", p: "This works instantly for anyone with a Teams account — no Azure app registration, no API keys, no admin approval. It's a deep-link into Teams." },
    ],
  },
  {
    id: "linkedin",
    icon: Linkedin,
    title: "LinkedIn Inbox (auto-updates via Zapier)",
    superadmin: true,
    steps: [
      { h: "What it is", p: "A SuperAdmin-only page (sidebar → LinkedIn) that receives new LinkedIn Lead Gen Form submissions automatically. Every incoming lead lands unassigned, waiting for you to allocate to a team member." },
      { h: "One-time setup", p: "Sidebar → LinkedIn → 'Zapier setup' button. Click 'Generate' to create your webhook secret, then copy the URL + secret. In Zapier: Trigger = LinkedIn Ads → New Lead Gen Form Response; Action = Webhooks → POST → paste the URL, add header `X-Webhook-Secret` = the secret, map fields to `name`, `phone`, `email`, `company`, `designation`, `notes`. Turn on. Done." },
      { h: "Sidebar badge", p: "The 'LinkedIn' nav item shows a blue badge with the count of unassigned leads — you'll always know when new ones arrive. Refreshes every 30 seconds." },
      { h: "Allocate to a member", p: "Check one or many rows → 'Assign to member' dropdown → click Assign. Leads move to that member's regular list and disappear from the inbox." },
      { h: "Automatic duplicate skip", p: "If a lead with the same phone/email already exists in your CRM (in ANY source), the webhook returns 'duplicate' and skips the insert — no cleanup needed." },
      { h: "Regenerate secret", p: "If your secret leaks, click 'Regenerate' on the setup panel — the old one stops working instantly. Update Zapier with the new secret and you're back in business." },
      { h: "No automation tool? Import a CSV.", p: "Prefer to skip Zapier entirely? On the LinkedIn Inbox page click 'Import CSV / Excel' — every row of a Sales Navigator export lands unassigned in the inbox with source=LinkedIn. Same allocation flow as webhook leads." },
    ],
  },
  {
    id: "security",
    icon: Shield,
    title: "Security & access control",
    superadmin: false,
    steps: [
      { h: "Role scoping", p: "Members only see the leads assigned to them. SuperAdmin sees everything and can reassign." },
      { h: "Brute-force protection", p: "Five wrong login attempts locks the account for 15 minutes." },
      { h: "Token in localStorage", p: "Your JWT is stored securely and sent on every API call. Sign out clears it." },
    ],
  },
  {
    id: "shortcuts",
    icon: KeyRound,
    title: "Tips & shortcuts",
    superadmin: false,
    steps: [
      { h: "Global search", p: "Focus the top-bar search and press Enter to jump to Leads filtered by your query." },
      { h: "Kanban drag", p: "Fastest way to move a lead through stages — no need to open it." },
      { h: "Bulk reassign", p: "Select many rows on the Leads page → SuperAdmin picks a member in the bulk bar → one API call reassigns all of them." },
      { h: "Undo a bad import", p: "Uploaded 200 messy rows? Head to Upload Leads → Import history → Undo import. Done in two clicks." },
      { h: "Restart the tour", p: "The 60-second product tour can be replayed from the button at the top of this Guide page — perfect when you're onboarding a new rep." },
      { h: "Export", p: "Leads page → 'Export CSV' downloads everything currently in your scope (respecting filters is coming soon)." },
    ],
  },
];

export default function Guide() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(SECTIONS[0].id);

  const launchTour = () => {
    if (user?.id) clearTourFlag(user.id);
    // Tour anchors live in the sidebar (always mounted). Kick it off directly.
    startTour({ userId: user?.id, isAdmin });
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return SECTIONS.filter((s) => {
      if (s.superadmin && !isAdmin) return false;
      if (!ql) return true;
      if (s.title.toLowerCase().includes(ql)) return true;
      return s.steps.some((st) => (st.h + " " + st.p).toLowerCase().includes(ql));
    });
  }, [q, isAdmin]);

  return (
    <div className="space-y-6" data-testid="guide-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Help</p>
          <h1 className="text-xl font-semibold mt-1 text-slate-900">Guide & tutorials</h1>
          <p className="text-slate-500 mt-1 text-sm">Everything CERF LeadFlow can do — search or scan the sections below.</p>
        </div>
        <Button
          onClick={launchTour}
          className="bg-[#1D63ED] hover:bg-[#1854D9] text-white"
          data-testid="restart-tour-btn"
        >
          <PlayCircle className="w-4 h-4 mr-2" /> Restart product tour
        </Button>
      </header>

      <div className="relative max-w-xl">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the guide…"
          className="pl-9 h-10 bg-white border-slate-200"
          data-testid="guide-search"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* TOC */}
        <aside className="lg:col-span-1">
          <div className="solid-panel p-3 sticky top-16" data-testid="guide-toc">
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setActive(s.id);
                  document.getElementById(`sec-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`w-full text-left flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors ${active === s.id ? "bg-[#E7EFFE] text-[#1D63ED] font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                data-testid={`guide-toc-${s.id}`}
              >
                <s.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{s.title}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Content */}
        <div className="lg:col-span-3 space-y-4">
          {filtered.length === 0 && (
            <div className="solid-panel p-8 text-center text-slate-500">No sections match "{q}".</div>
          )}
          {filtered.map((s) => (
            <section id={`sec-${s.id}`} key={s.id} className="solid-panel p-6" data-testid={`guide-sec-${s.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-[#E7EFFE] flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-[#1D63ED]" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    {s.title}
                    {s.superadmin && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 font-medium">SuperAdmin</span>
                    )}
                  </h2>
                </div>
              </div>
              <ol className="space-y-4">
                {s.steps.map((st, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center text-[11px] font-semibold text-slate-700">{i + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">{st.h}</p>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{st.p}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}

          <div className="solid-panel p-6 flex items-center gap-4">
            <MessageSquare className="w-5 h-5 text-[#1D63ED]" />
            <div>
              <p className="text-sm font-medium text-slate-800">Need more help?</p>
              <p className="text-xs text-slate-500">Ping your SuperAdmin or your CERF admin — they can regenerate your login token, reassign leads, or trigger reminders on your behalf.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
